<template>
    <label class="role-portrait-upload" :class="{ 'is-disabled': disabled, 'role-portrait-upload--makeup': tone === 'makeup' }">
        <span>本人真实照片 <b v-if="required">*</b></span>
        <span class="role-portrait-upload__box">
            <input
                ref="fileInput"
                type="file"
                accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                :disabled="disabled || validating"
                @change="selectFile" />
            <img v-if="previewUrl" :src="previewUrl" alt="已选择的本人真实照片预览" />
            <span v-else class="role-portrait-upload__placeholder">
                <i aria-hidden="true">+</i>
                <strong>{{ disabled ? disabledMessage : '点击上传照片' }}</strong>
            </span>
            <span v-if="previewUrl" class="role-portrait-upload__replace">{{ validating ? '正在校验…' : '重新选择照片' }}</span>
            <button
                v-if="previewUrl"
                type="button"
                class="role-portrait-upload__remove"
                aria-label="删除本人真实照片"
                title="删除照片"
                :disabled="disabled || validating"
                @click.prevent.stop="removeFile">
                <i class="el-icon-delete" aria-hidden="true"></i>
            </button>
        </span>
        <small>JPG / PNG，最大 10MB，图片尺寸至少 600 × 600</small>
    </label>
</template>

<script>
import {
    getRolePortraitDimensionError,
    getRolePortraitFileError,
} from "@/tool/rolePortrait.cjs";

export default {
    name: "RolePortraitUpload",
    props: {
        value: { default: null },
        required: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        disabledMessage: { type: String, default: "当前暂不能更换照片" },
        tone: { type: String, default: "photographer" },
    },
    data() {
        return { previewUrl: "", previewFile: null, validating: false };
    },
    watch: {
        value: {
            immediate: true,
            handler(file) {
                if (file !== this.previewFile) this.setPreview(file);
            },
        },
    },
    beforeDestroy() {
        this.clearPreview();
    },
    methods: {
        clearPreview() {
            if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = "";
            this.previewFile = null;
        },
        setPreview(file) {
            this.clearPreview();
            if (!file) return;
            this.previewFile = file;
            this.previewUrl = URL.createObjectURL(file);
        },
        readFile(event) {
            const file = event && event.target && event.target.files ? event.target.files[0] : null;
            if (event && event.target) event.target.value = "";
            return file;
        },
        validateDimensions(file) {
            return new Promise((resolve) => {
                const objectUrl = URL.createObjectURL(file);
                const image = new Image();
                image.onload = () => {
                    URL.revokeObjectURL(objectUrl);
                    resolve(getRolePortraitDimensionError({ width: image.naturalWidth, height: image.naturalHeight }));
                };
                image.onerror = () => {
                    URL.revokeObjectURL(objectUrl);
                    resolve("图片无法读取，请重新选择 JPG 或 PNG 文件");
                };
                image.src = objectUrl;
            });
        },
        async validateFile(file) {
            const fileError = getRolePortraitFileError(file);
            if (fileError) return fileError;
            return this.validateDimensions(file);
        },
        async selectFile(event) {
            const file = this.readFile(event);
            if (!file || this.disabled || this.validating) return;
            this.validating = true;
            const error = await this.validateFile(file);
            if (this._isDestroyed || this._isBeingDestroyed) return;
            this.validating = false;
            if (error) {
                this.$emit("error", error);
                return;
            }
            this.setPreview(file);
            this.$emit("input", file);
            this.$emit("change", file);
        },
        removeFile() {
            if (this.disabled || this.validating) return;
            this.clearPreview();
            this.$emit("input", null);
            this.$emit("change", null);
        },
    },
};
</script>

<style scoped>
.role-portrait-upload { display: grid; width: min(128px, 100%); justify-self: start; gap: 7px; color: #5c5563; font-size: 12px; font-weight: 700; }
.role-portrait-upload b { color: #dc657f; }
.role-portrait-upload__box { position: relative; display: grid; width: 128px; min-height: 128px; place-items: center; overflow: hidden; color: #4a809e; background: #f8fafb; border: 1px dashed #a9cfdf; border-radius: 11px; cursor: pointer; }
.role-portrait-upload__box input { position: absolute; z-index: 2; inset: 0; width: 100%; height: 100%; padding: 0; opacity: 0; cursor: pointer; }
.role-portrait-upload__box img { display: block; width: 128px; height: 128px; object-fit: cover; }
.role-portrait-upload__placeholder { display: grid; justify-items: center; gap: 7px; padding: 12px; text-align: center; }
.role-portrait-upload__placeholder i { display: grid; width: 30px; height: 30px; place-items: center; color: #4f90ae; background: #fff; border: 1px solid #bad8e6; border-radius: 8px; font-size: 22px; font-style: normal; font-weight: 400; line-height: 1; }
.role-portrait-upload__placeholder strong { font-size: 12px; }
.role-portrait-upload__replace { position: absolute; z-index: 1; right: 9px; bottom: 9px; padding: 5px 8px; color: #fff; background: rgba(40, 51, 59, .72); border-radius: 6px; font-size: 10px; }
.role-portrait-upload__remove { position: absolute; z-index: 3; top: 8px; right: 8px; display: grid; width: 27px; height: 27px; place-items: center; padding: 0; color: #fff; background: rgba(73, 48, 61, .78); border: 0; border-radius: 50%; cursor: pointer; font-size: 13px; }
.role-portrait-upload__remove:hover { background: #d56886; }
.role-portrait-upload__remove:disabled { cursor: not-allowed; opacity: .65; }
.role-portrait-upload small { color: #857f76; font-size: 10px; font-weight: 400; line-height: 1.5; }
.role-portrait-upload.is-disabled .role-portrait-upload__box { color: #8c8990; background: #f3f4f5; border-color: #d9dde0; cursor: not-allowed; }
.role-portrait-upload.is-disabled .role-portrait-upload__box input { cursor: not-allowed; }
.role-portrait-upload--makeup { color: #5c5563; }
.role-portrait-upload--makeup .role-portrait-upload__box { color: #b3536e; background: #fffafb; border-color: #e4aab9; }
.role-portrait-upload--makeup .role-portrait-upload__placeholder i { color: #c55d78; border-color: #edbdc9; }
.role-portrait-upload--makeup .role-portrait-upload__remove:hover { background: #d56886; }
@media (max-width: 420px) { .role-portrait-upload, .role-portrait-upload__box, .role-portrait-upload__box img { width: 120px; }.role-portrait-upload__box, .role-portrait-upload__box img { min-height: 120px; height: 120px; } }
</style>
